// Tenant (white-label) subdomain detection for the web app.
//
// `trainercompany.getnubod.com` → "trainercompany". Our own system hosts
// (app, www, api, …) and any non-getnubod host (localhost, Heroku review
// apps) return null, so they render the default Nubod brand. For local
// development you can force a tenant with `?brand=<slug>` on any URL.

const BASE_DOMAIN = 'getnubod.com';

// Hosts we serve ourselves — never a tenant slug. Mirrors (a subset of)
// the reserved list the API enforces in helpers/slug.js. The API is the
// real gatekeeper; this just avoids a pointless branding fetch for our
// own hosts.
const SYSTEM_HOSTS = new Set([
  'app', 'www', 'api', 'staging', 'stage', 'dev', 'admin', 'dashboard',
]);

/**
 * Resolve the tenant slug from the current location, or null if this
 * isn't a tenant subdomain.
 *
 * @param {Location|{hostname:string,search:string}} [loc]
 * @returns {string|null}
 */
export function getTenantSlug(
  loc = typeof window !== 'undefined' ? window.location : null
) {
  if (!loc) return null;

  // Dev/preview override: ?brand=acme works on any host (localhost too).
  try {
    const override = new URLSearchParams(loc.search || '').get('brand');
    if (override) return override.trim().toLowerCase() || null;
  } catch (_) {
    // ignore a malformed query string
  }

  const host = (loc.hostname || '').toLowerCase();
  if (!host.endsWith(`.${BASE_DOMAIN}`)) return null;

  // The label before `.getnubod.com`. A tenant slug is a SINGLE label;
  // anything deeper (a.b.getnubod.com) isn't a tenant we serve.
  const prefix = host.slice(0, host.length - (BASE_DOMAIN.length + 1));
  if (!prefix || prefix.includes('.')) return null;
  if (SYSTEM_HOSTS.has(prefix)) return null;
  return prefix;
}
